import { useCallback, useEffect, useMemo } from "react";
import { FormikErrors, FormikTouched } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import {
	addNewAttributeValue,
	setAttributePickerValue,
	setAttributeSubmitValue,
} from "@redux/actions/verification-form-actions";
import {
	getAutoSubmitPrefilledData,
	getDontRenderUI,
	getLocalizeStrings,
	getTMXAutoSubmit
} from "@redux/selectors/verification-configuration-selectors";
import { AttributeFormState } from "@idw-types/state/attribute-form-state";
import { AttributeContainer } from "../../common/attribute-container/attribute-container";
import { Logger } from "@utils/logger";


type AttributeListComponentProps = {
	errors: FormikErrors<any>;
	touched: FormikTouched<any>;
	isSubmitting: boolean;
	attributeFormStates: Array<AttributeFormState>;
	setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
	setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
	submitForm: () => Promise<void>;
}

export const AttributeListComponent = (props: AttributeListComponentProps) => {
	const {
		errors,
		touched,
		isSubmitting,
		attributeFormStates,
		setFieldTouched,
		setFieldValue,
		submitForm
	} = props;

	const dispatch = useDispatch();

	const localizeStrings = useSelector(getLocalizeStrings);
	const dontRenderUI = useSelector(getDontRenderUI);
	const tmxAutoSubmit = useSelector(getTMXAutoSubmit);
	const autoSubmitPrefilledData = useSelector(getAutoSubmitPrefilledData);

	useEffect(() => {
		if (autoSubmitPrefilledData && attributeFormStates.length > 0) {
			const isEditable = attributeFormStates.some(attribute => attribute.isEditable);

			if (!isEditable && (!tmxAutoSubmit || !dontRenderUI)) {
				submitForm();
				Logger.info('[AutoSubmitPrefilledData] Form auto submitted with prefilled data');
			}
		}
	}, [autoSubmitPrefilledData, tmxAutoSubmit, dontRenderUI]);

	const handleAddNewSubmitValue = useCallback((payload: { attribute: AttributeFormState; inputData: any }) => {
		dispatch(addNewAttributeValue(payload.attribute, payload.inputData))
	}, []);

	const handleUpdateSubmitValue = useCallback((payload: { attribute: AttributeFormState; inputData: any }) => {
		dispatch(setAttributeSubmitValue(payload.attribute, payload.inputData));
	}, []);

	const handleUpdatePickerValue = useCallback((payload: { attribute: AttributeFormState; pickerData: any }) => {
		dispatch(setAttributePickerValue(payload.attribute, payload.pickerData));
	}, []);

	// const handleMessage = (payload: any) => {
	// 	dispatch(setVerificationMessage(payload));
	// };

	const {
		attributePinIndexMap,
		attributePinIndexMapKeys
	} = useMemo(() => {
		const pinIndexMap: { [key: string]: number } = {};
		attributeFormStates.forEach(attribute => {
			if (attribute.inBandPinMode) {
				pinIndexMap[attribute.id] = attributeFormStates.length + Object.entries(pinIndexMap).length;
			}
		});
		return {
			attributePinIndexMap: pinIndexMap,
			attributePinIndexMapKeys: Object.keys(pinIndexMap)
		};
	}, [attributeFormStates]);

	return (
		<>
			{attributeFormStates.map((attribute, index) => (
				<AttributeContainer
					key={`${attribute.id}-${index}`}
					index={index}
					localizeStrings={localizeStrings}
					errors={errors}
					touched={touched}
					pinFieldShouldBeAutofocused={attributePinIndexMapKeys.length > 0 && `${attribute.id}` === attributePinIndexMapKeys[0]}
					attributeValuesPinIndex={attributePinIndexMap[attribute.id]}
					setFieldTouched={setFieldTouched}
					setFieldValue={setFieldValue}
					attributeFormState={attribute}
					handleAddNewSubmitValue={handleAddNewSubmitValue}
					handleUpdateSubmitValue={handleUpdateSubmitValue}
					handleUpdatePickerValue={handleUpdatePickerValue}
					isSubmitting={isSubmitting}
				/>
			))}
		</>
	);
};